import React, { useState } from "react";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

export default function AnalyticsReset() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const doReset = async () => {
    setResetting(true);
    try {
      const res = await adminApi.resetAnalytics();
      toast({ title: "Analytics data cleared", description: `${res.total} record(s) removed. You're starting fresh.` });
      window.dispatchEvent(new Event("inbox-updated"));
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast({ title: "Couldn't reset data", variant: "destructive" });
      setResetting(false);
    }
  };

  return (
    <div className="mt-10 rounded-2xl border border-red-200 bg-red-50/50 p-6" data-testid="analytics-danger-zone">
      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle size={20} /></span>
          <div>
            <h2 className="font-display font-bold text-navy">Reset analytics data</h2>
            <p className="text-sm text-slate-500 max-w-xl mt-0.5">Permanently deletes all enquiries, quote requests, guide downloads and Cassie chatbot messages so you start fresh at go-live. Newsletter subscribers and your homepage content are kept. This cannot be undone.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-600 hover:text-white shrink-0" data-testid="analytics-reset-open">
          <Trash2 size={15} className="mr-1.5" /> Reset data
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
        <AlertDialogContent data-testid="analytics-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all analytics data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>all enquiries, quote requests, guide downloads and Cassie chat messages</strong>. Subscribers and homepage content are kept. This cannot be undone.
              <br /><br />Type <strong>RESET</strong> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET" data-testid="analytics-reset-confirm-input" className="mt-1" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="analytics-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "RESET" || resetting}
              onClick={(e) => { e.preventDefault(); doReset(); }}
              data-testid="analytics-reset-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              {resetting ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Clearing…</> : "Clear all data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
